import React, { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Icon } from "components/SvgIcons";
import { PaginationWrap, Item, ItemPage, ItemPrev, ItemNext, ItemDots } from "./styled";

import { getElementsPerPage } from "variables/recipesVariables";
import { useRenderDevice } from "helpers/hooks/useRenderDevice";

export const initialPage = 1;

const Pagination = ({
  handleDraftPage,
  _page = 1,
  countOfElements,
  stepClick,
  destination,
  elementsPerPage = getElementsPerPage(),
  isDraft = false,
}) => {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(Number(router.query.page) || _page);

  const isDesktop = useRenderDevice({ min: 768 });
  const _destination = destination ? destination : "search";
  const pushPage = (page) =>
    router.push(
      {
        pathname: `/${_destination}`,
        query: { ...router.query, page },
      },
      undefined,
      { shallow: true }
    );

  const handleClick = useCallback(
    (page) => {
      setCurrentPage(page);
      stepClick(page);
      if (!isDraft) {
        pushPage(page);
      } else {
        handleDraftPage(page);
      }
    },
    [stepClick]
  );

  useEffect(() => {
    if (currentPage !== _page) {
      setCurrentPage(Number(router.query.page) || _page);
    }
  }, [countOfElements]);

  const countOfPagesAfterCurrent = isDesktop ? 2 : 1;
  const countOfPagesBeforeCurrent = isDesktop ? 2 : 1;
  const maxShowingPages = countOfPagesAfterCurrent + countOfPagesBeforeCurrent + 1;

  const { countOfPages, isFirstPage, isLastPage, pages } = usePages({
    currentPage,
    countOfElements,
    elementsPerPage,
    maxShowingPages,
    countOfPagesBeforeCurrent,
    countOfPagesAfterCurrent,
  });

  return (
    countOfPages > 1 && (
      <PaginationWrap>
        <ItemPrev disabled={isFirstPage} onClick={() => handleClick(currentPage - 1)}>
          <ItemPage>
            <Icon icon="arrow_left" />
            {isDesktop && "Back"}
          </ItemPage>
        </ItemPrev>
        {currentPage > countOfPagesBeforeCurrent + 2 && countOfPages > maxShowingPages + 1 && (
          <>
            <Item>
              <ItemPage onClick={() => handleClick(initialPage)}>{initialPage}</ItemPage>
            </Item>
            <Item>
              <ItemDots>...</ItemDots>
            </Item>
          </>
        )}
        {pages.map((item) => (
          <Item key={item} disabled={item === currentPage}>
            <ItemPage className={item === currentPage ? "active" : ""} onClick={() => handleClick(item)}>
              {item}
            </ItemPage>
          </Item>
        ))}
        {currentPage < countOfPages - countOfPagesAfterCurrent - 1 && countOfPages > maxShowingPages + 1 && (
          <>
            <Item>
              <ItemDots>...</ItemDots>
            </Item>
            <Item>
              <ItemPage onClick={() => handleClick(countOfPages)}>{countOfPages}</ItemPage>
            </Item>
          </>
        )}
        <ItemNext disabled={isLastPage} onClick={() => handleClick(currentPage + 1)}>
          <ItemPage>
            {isDesktop && "Next"}
            <Icon icon="arrow_right" />
          </ItemPage>
        </ItemNext>
      </PaginationWrap>
    )
  );
};

const usePages = ({
  countOfElements,
  currentPage,
  elementsPerPage,
  maxShowingPages,
  countOfPagesBeforeCurrent,
  countOfPagesAfterCurrent,
}) => {
  const countOfPages = Math.ceil(countOfElements / elementsPerPage);
  const isFirstPage = currentPage === initialPage;
  const isLastPage = currentPage === countOfPages;

  let rangeStart = initialPage;
  let rangeEnd = countOfPages;

  if (countOfPages > maxShowingPages) {
    rangeStart = currentPage > countOfPagesBeforeCurrent ? currentPage - countOfPagesBeforeCurrent : initialPage;

    rangeStart =
      currentPage + countOfPagesAfterCurrent > countOfPages
        ? currentPage - countOfPagesBeforeCurrent - (currentPage + countOfPagesAfterCurrent - countOfPages)
        : rangeStart;

    rangeEnd = rangeStart + maxShowingPages - 1 > countOfPages ? countOfPages : rangeStart + maxShowingPages - 1;
  }

  const [pages, setPages] = useState([initialPage]);

  useEffect(() => {
    const pagesArray = [];
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pagesArray.push(i);
    }
    setPages(pagesArray);
  }, [countOfPages, currentPage]);

  return {
    pages,
    isLastPage,
    isFirstPage,
    countOfPages,
  };
};

export default Pagination;
